import axios from "axios";
import {
  GeneratedDocumentData,
  DocumentTheme,
  Section,
  Block,
} from "../types/documentTypes";
import {
  DocumentGenerationData,
  Client,
  PromptTemplate,
  ReferenceDocument,
} from "../types/document-form";
import { API_URL, API_KEY, ENDPOINTS } from "../lib/constants";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { convertContentToBlocks } from "../utils/documentUtils";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
    "x-api-key": API_KEY,
  },
});

// API Health Check
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const response = await api.get("/api/health");
    return response.status === 200;
  } catch (error) {
    console.warn("API health check failed:", error);
    return false;
  }
};

// Add a function to get user ID
const getUserId = async (): Promise<string | null> => {
  try {
    // Use the already imported supabase client
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return user?.id || null;
  } catch (error) {
    console.warn("Unable to get user ID:", error);
    return null;
  }
};

// Document Generation Service
export interface GenerationCallbacks {
  onStarted?: (data: any) => void;
  onProgress?: (data: any) => void; // Add the missing progress callback
  onResearch?: (data: any) => void;
  onSummary?: (data: any) => void;
  onSection?: (data: any) => void;
  onCompleted?: (data: any) => void;
  onEnd?: (data: any) => void;
  onError?: (error: any) => void;
}

export const generateDocument = async (
  documentData: DocumentGenerationData,
  preserveOriginalContent: boolean,
  callbacks: GenerationCallbacks,
  eventSourceRef: React.MutableRefObject<EventSource | null>,
  documentIdRef?: React.MutableRefObject<string | null>
): Promise<EventSource> => {
  // Generate a temporary local ID, but prioritize server-returned ID
  let documentId = `doc_${Date.now()}_${Math.random()
    .toString(36)
    .substr(2, 9)}`;

  // Initialize the document ID reference if provided
  if (documentIdRef) {
    documentIdRef.current = documentId;
  }

  console.log("🚀 Starting document generation...");
  console.log("Temporary Document ID:", documentId);
  console.log("Input data:", documentData);
  console.log("Preserve original content:", preserveOriginalContent);

  // Check for required data
  if (!documentData.title) {
    throw new Error("Document title is required");
  }

  if (!documentData.client?.name) {
    throw new Error("Client name is required");
  }

  // Close any existing EventSource
  if (
    eventSourceRef?.current &&
    typeof eventSourceRef.current.close === "function"
  ) {
    console.log("🔌 Closing existing EventSource connection");
    eventSourceRef.current.close();
    eventSourceRef.current = null;
  }

  // Generate a consistent document ID that can be used for storage and retrieval
  console.log("Generated document ID:", documentId);

  // Refresh cost data before generating.
  //
  // The tender reads every rate and cost figure from offer_cost_breakdown.cost_data — a stored
  // snapshot written only when a calculation runs. Nothing in the generation path recalculates
  // it, so any offer edited after the last calculation (a manually entered shoulder rate, a
  // corrected non-energy charge) rendered from stale data: correct in every UI step, which reads
  // the offer live, but wrong or blank in the generated tender. Recalculating here guarantees
  // the document reflects the offers as they stand right now.
  //
  // Non-fatal by design: if this fails we still generate from the existing snapshot rather than
  // blocking the user's document entirely.
  if (documentData.tenderId) {
    try {
      console.log("🔄 Refreshing cost data before generation...");
      const { calculateTenderCosts } = await import("./calculationApiService");
      const refresh = await calculateTenderCosts(documentData.tenderId);
      if (!refresh?.success) {
        console.warn("⚠️ Cost refresh reported failure — generating from existing snapshot:", refresh?.errors);
      }
    } catch (err) {
      console.warn("⚠️ Cost refresh failed — generating from existing snapshot:", err);
    }
  }

  // Get user ID for Pinecone filtering
  const userId = await getUserId();

  // Prepare the request data for the API
  const apiRequestData = {
    client: documentData.client,
    template: documentData.template,
    requirements: documentData.requirements,
    resources: [], // Add any resources if needed
    aiPrompts: documentData.aiPrompts,
    project: documentData.project,
    referenceDocument: documentData.referenceDocument,
    preserveOriginalContent: preserveOriginalContent,
    selectedKnowledgeDocuments:
      documentData.requirements?.selectedKnowledgeDocuments || [],
    documentRequirements: documentData.requirements?.documentRequirements || {},
    userId: userId, // Add userId for Pinecone filtering
    tenderId: documentData.tenderId || null, // Tender ID for cost data lookup
  };

  console.log("📤 Request data prepared:", apiRequestData);
  console.log(
    "🔍 Knowledge base documents:",
    apiRequestData.selectedKnowledgeDocuments
  );
  console.log("📝 Document requirements:", apiRequestData.documentRequirements);

  // Continue with the rest of the function...

  try {
    // Check if we have knowledge base documents to include
    if (
      documentData.requirements?.selectedKnowledgeDocuments &&
      documentData.requirements.selectedKnowledgeDocuments.length > 0
    ) {
      console.log(
        `📚 Including ${documentData.requirements.selectedKnowledgeDocuments.length} knowledge base documents`
      );

      // Import the knowledge service to get document details
      const { knowledgeService } = await import(
        "../../knowledge/knowledgebase/features/knowledge/services/knowledgeService"
      );

      try {
        // Get full document data from knowledge base (include default documents for proposal generation)
        const allDocs = await knowledgeService.getDocuments(undefined, true);
        const selectedDocs = allDocs.filter((doc) =>
          documentData.requirements!.selectedKnowledgeDocuments!.includes(
            doc.id
          )
        );

        console.log(
          `✅ Found ${selectedDocs.length} knowledge base documents:`,
          selectedDocs.map((d) => d.name)
        );

        // Note: Document content will be retrieved by the backend from Pinecone
        // We just need to pass the document IDs
      } catch (error) {
        console.warn("⚠️  Failed to fetch knowledge base documents:", error);
        // Continue with generation but log the warning
      }
    }

    // Create the EventSource connection directly to the streaming endpoint
    const streamUrl = `${API_URL}/api/tender/proposals/stream`;
    console.log("🌊 Creating direct EventSource connection to:", streamUrl);

    // Since the server expects a POST but EventSource only does GET,
    // we need to use a different approach. Let's create a custom EventSource-like implementation
    // using fetch with a ReadableStream

    // Create the POST request for streaming with extended timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minutes timeout

    const response = await fetch(streamUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "scopingai",
        "Cache-Control": "no-cache",
        Connection: "keep-alive",
      },
      body: JSON.stringify(apiRequestData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    console.log("📝 Connected to streaming endpoint");

    // Initialize document storage immediately with real data
    // Validate that client ID is not the same as user ID
    const validClientId =
      apiRequestData.client.id !== userId ? apiRequestData.client.id : null;

    if (apiRequestData.client.id === userId) {
      console.warn(
        "🚨 Client ID matches User ID - this is incorrect! Setting to null."
      );
    }

    const initialDocumentData = {
      documentData: {
        title: apiRequestData.project.title,
        client: apiRequestData.client.name,
        type: "Scoping Document",
        sections: [],
      },
      sections: [],
      overview: "",
      status: "generating",
      documentId: documentId,
      clientId: validClientId,
      startedAt: new Date().toISOString(),
      preserveOriginalContent: preserveOriginalContent,
      knowledgeBaseUsed:
        (apiRequestData.selectedKnowledgeDocuments?.length || 0) > 0,
      knowledgeDocumentCount:
        apiRequestData.selectedKnowledgeDocuments?.length || 0,
    };
    sessionStorage.removeItem("scopingai_generated_url");

    // Store initial data
    sessionStorage.setItem(
      `document_${documentId}`,
      JSON.stringify(initialDocumentData)
    );
    const finalUrl = `/tender/documents/generated?id=${documentId}`;
    console.log("🔗 Final URL ready for manual navigation:", finalUrl);
    sessionStorage.setItem("scopingai_generated_url", finalUrl);

    // Process the streaming response
    if (!response.body) {
      throw new Error("No response body for streaming");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    // Create a mock EventSource object to maintain compatibility
    const mockEventSource = new EventTarget() as EventSource & EventTarget;

    // Track if the stream is closed
    let isClosed = false;

    // Add the close method to the mock EventSource
    (mockEventSource as any).close = () => {
      console.log("🔌 Mock EventSource close() called");
      isClosed = true;
      (mockEventSource as any).readyState = 2; // CLOSED
      // Cancel the fetch request if possible
      if (controller) {
        controller.abort();
      }
    };

    // Add readyState property
    (mockEventSource as any).readyState = 1; // OPEN

    eventSourceRef.current = mockEventSource as EventSource;

    // Process the stream with better error handling
    const processStream = async () => {
      let lastHeartbeat = Date.now();
      const heartbeatInterval = setInterval(() => {
        const now = Date.now();
        if (now - lastHeartbeat > 120000) {
          // 2 minutes without data
          console.warn(
            "⚠️ No data received for 2 minutes, connection may be stale"
          );
          if (callbacks.onProgress) {
            callbacks.onProgress({
              progress: Math.min(((now - lastHeartbeat) / 1000 / 60) * 10, 95), // Estimate progress
              message: "Still processing... This may take a few more minutes",
            });
          }
        }
      }, 30000); // Check every 30 seconds

      try {
        while (true) {
          // Check if the stream was closed
          if (isClosed) {
            console.log(
              "🔌 Stream processing stopped - EventSource was closed"
            );
            clearInterval(heartbeatInterval);
            break;
          }

          const { done, value } = await reader.read();
          if (done) {
            clearInterval(heartbeatInterval);
            break;
          }

          lastHeartbeat = Date.now();
          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              const eventType = line.substring(7).trim();

              // Handle heartbeat events to keep connection alive
              if (eventType === "heartbeat") {
                console.log("💓 Received heartbeat, connection is alive");
                continue;
              }

              // Handle completed events - this is the final completion
              if (eventType === "completed") {
                console.log("🎉 Received COMPLETED event - final completion!");
                // This will be handled in the data parsing below
              }
              continue;
            }

            if (line.startsWith("data: ")) {
              const data = line.substring(6).trim();
              if (data) {
                try {
                  const parsedData = JSON.parse(data);

                  // CRITICAL DEBUG: Log all events that might trigger redirects
                  if (
                    parsedData.complete ||
                    parsedData.status === "completed" ||
                    parsedData.proposal
                  ) {
                    console.log("🚨 CRITICAL EVENT DETECTED:", {
                      hasProposal: !!parsedData.proposal,
                      complete: parsedData.complete,
                      status: parsedData.status,
                      proposalSections:
                        parsedData.proposal?.sections?.length || 0,
                      message: parsedData.message,
                    });
                  }

                  // Determine event type based on data structure

                  // PRIORITY: Handle progress events first (real-time updates)
                  if (
                    parsedData.progress !== undefined ||
                    parsedData.current_step ||
                    (parsedData.message && parsedData.status === "running")
                  ) {
                    console.log("🔥 PROGRESS EVENT DETECTED:", parsedData);
                    console.log(
                      "🔥 Progress:",
                      parsedData.progress,
                      "Step:",
                      parsedData.current_step,
                      "Message:",
                      parsedData.message
                    );
                    if (callbacks.onProgress) {
                      callbacks.onProgress(parsedData);
                    }
                  } else if (
                    parsedData.message === "Starting document generation..."
                  ) {
                    console.log("🚀 Started event:", parsedData);

                    // Update documentId if server provides one
                    if (
                      parsedData.documentId &&
                      parsedData.documentId !== documentId
                    ) {
                      console.log(
                        "📝 Updating document ID from server:",
                        parsedData.documentId
                      );
                      documentId = parsedData.documentId;

                      // Update the reference if provided
                      if (documentIdRef) {
                        documentIdRef.current = documentId;
                        console.log(
                          "📝 Updated document ID reference:",
                          documentId
                        );
                      }
                    }

                    const callbackData = {
                      ...parsedData,
                      documentId: documentId,
                    };
                    if (callbacks.onStarted) callbacks.onStarted(callbackData);
                  } else if (
                    parsedData.status === "started" &&
                    parsedData.message?.includes("Researching")
                  ) {
                    console.log("🔍 Research started event:", parsedData);
                    if (callbacks.onResearch) callbacks.onResearch(parsedData);
                  } else if (
                    parsedData.status === "completed" &&
                    parsedData.content
                  ) {
                    if (
                      parsedData.content.includes("summary") ||
                      parsedData.content.includes("executive")
                    ) {
                      console.log("📋 Summary event:", parsedData);

                      // Update stored data with summary
                      const storedData = JSON.parse(
                        sessionStorage.getItem(`document_${documentId}`) || "{}"
                      );
                      storedData.overview = parsedData.content;
                      storedData.status = "summarizing";
                      sessionStorage.setItem(
                        `document_${documentId}`,
                        JSON.stringify(storedData)
                      );

                      if (callbacks.onSummary) callbacks.onSummary(parsedData);
                    } else {
                      console.log("🔍 Research completed event:", parsedData);

                      // Update stored data with research
                      const storedData = JSON.parse(
                        sessionStorage.getItem(`document_${documentId}`) || "{}"
                      );
                      storedData.research = parsedData.content;
                      storedData.status = "researching";
                      sessionStorage.setItem(
                        `document_${documentId}`,
                        JSON.stringify(storedData)
                      );

                      if (callbacks.onResearch)
                        callbacks.onResearch(parsedData);
                    }
                  } else if (parsedData.section) {
                    console.log("📄 Section event:", parsedData);

                    // Update stored data with new section
                    const storedData = JSON.parse(
                      sessionStorage.getItem(`document_${documentId}`) || "{}"
                    );
                    if (!storedData.sections) storedData.sections = [];

                    // Convert section to the expected format
                    const newSection = {
                      id:
                        parsedData.section.id ||
                        `section-${storedData.sections.length + 1}`,
                      title:
                        parsedData.section.title ||
                        `Section ${storedData.sections.length + 1}`,
                      content: parsedData.section.content || "",
                      blocks: convertContentToBlocks(
                        parsedData.section.content || ""
                      ),
                      status: "completed",
                      order: storedData.sections.length + 1,
                    };

                    storedData.sections.push(newSection);

                    // Also update the main document sections
                    if (!storedData.documentData.sections)
                      storedData.documentData.sections = [];
                    storedData.documentData.sections.push({
                      id: newSection.id,
                      title: newSection.title,
                      content: newSection.content,
                    });

                    storedData.status = "generating_sections";
                    sessionStorage.setItem(
                      `document_${documentId}`,
                      JSON.stringify(storedData)
                    );

                    if (callbacks.onSection) callbacks.onSection(parsedData);
                  } else if (
                    parsedData.proposal ||
                    parsedData.complete === true ||
                    parsedData.status === "completed"
                  ) {
                    console.log(
                      "✅ COMPLETED event (final completion):",
                      parsedData
                    );

                    // Update documentId if server provides one
                    if (
                      parsedData.documentId &&
                      parsedData.documentId !== documentId
                    ) {
                      console.log(
                        "📝 Updating document ID from server (completed):",
                        parsedData.documentId
                      );
                      documentId = parsedData.documentId;

                      // Update the reference if provided
                      if (documentIdRef) {
                        documentIdRef.current = documentId;
                        console.log(
                          "📝 Updated document ID reference (completed):",
                          documentId
                        );
                      }
                    }

                    // Update stored data with completion status
                    const storedData = JSON.parse(
                      sessionStorage.getItem(`document_${documentId}`) || "{}"
                    );
                    storedData.status = "completed";
                    storedData.completedAt = new Date().toISOString();
                    sessionStorage.setItem(
                      `document_${documentId}`,
                      JSON.stringify(storedData)
                    );

                    // Update callbacks with the correct document ID
                    const callbackData = {
                      ...parsedData,
                      documentId: documentId,
                      complete: true,
                      status: "completed",
                    };
                    if (callbacks.onCompleted)
                      callbacks.onCompleted(callbackData);
                  } else if (parsedData.message === "Stream complete") {
                    console.log("🏁 End event:", parsedData);

                    // Update documentId if server provides one
                    if (
                      parsedData.documentId &&
                      parsedData.documentId !== documentId
                    ) {
                      console.log(
                        "📝 Updating document ID from server (end):",
                        parsedData.documentId
                      );
                      documentId = parsedData.documentId;

                      // Update the reference if provided
                      if (documentIdRef) {
                        documentIdRef.current = documentId;
                        console.log(
                          "📝 Updated document ID reference (end):",
                          documentId
                        );
                      }
                    }

                    // Update callbacks with the correct document ID
                    const callbackData = {
                      ...parsedData,
                      documentId: documentId,
                    };
                    if (callbacks.onEnd) callbacks.onEnd(callbackData);
                  } else if (parsedData.error) {
                    console.error("❌ Error event:", parsedData);
                    if (callbacks.onError) {
                      callbacks.onError({
                        message: parsedData.error,
                        details: parsedData.details,
                        type: "generation_error",
                      });
                    }
                  }
                } catch (parseError) {
                  console.warn("⚠️  Failed to parse streaming data:", data);
                }
              }
            }
          }
        }
      } catch (streamError) {
        clearInterval(heartbeatInterval);
        console.error("❌ Stream processing error:", streamError);

        // Check if it's an abort error (user cancelled)
        if (streamError instanceof Error && streamError.name === "AbortError") {
          console.log("🛑 Stream was cancelled by user");
          return;
        }

        // Check if it's a network error vs server error
        const isNetworkError =
          streamError instanceof TypeError ||
          (streamError instanceof Error &&
            streamError.message.includes("fetch"));

        if (isNetworkError) {
          console.log(
            "🔄 Network error detected - attempting to retrieve cached result..."
          );

          // Try to get cached result from server
          try {
            const cacheResponse = await fetch(
              `${API_URL}/api/tender/proposals/result/${documentId}`,
              {
                headers: {
                  "x-api-key": "scopingai",
                },
              }
            );

            if (cacheResponse.ok) {
              const cachedData = await cacheResponse.json();
              console.log("✅ Retrieved cached result:", cachedData);

              if (callbacks.onCompleted) {
                callbacks.onCompleted({
                  ...cachedData.proposal,
                  documentId: documentId,
                  complete: true,
                  status: "completed",
                  cached: true,
                });
              }
              return;
            }
          } catch (cacheError) {
            console.warn("⚠️ Failed to retrieve cached result:", cacheError);
          }
        }

        if (callbacks.onError) {
          callbacks.onError({
            message: isNetworkError
              ? "Connection lost. The generation may still be running on the server. Please try refreshing the page in a few minutes."
              : streamError instanceof Error
              ? streamError.message
              : "Stream processing failed",
            type: isNetworkError ? "network_error" : "stream_error",
          });
        }
      }
    };

    // Start processing the stream
    processStream();

    return mockEventSource as EventSource;
  } catch (error) {
    console.error("❌ Failed to start document generation:", error);

    if (callbacks.onError) {
      callbacks.onError({
        message:
          error instanceof Error ? error.message : "Failed to start generation",
        type: "initialization_error",
      });
    }

    throw error;
  }
};

// Document Management Services
export async function fetchDocument(documentId: string) {
  try {
    console.log(
      "fetchDocument called with ID:",
      documentId,
      "type:",
      typeof documentId
    );

    // Fetch document metadata from database
    const { data: dbData, error: dbError } = await supabase
      .from("scopingai_documents")
      .select("*")
      .eq("id", documentId)
      .single();

    console.log("Database query result:", { dbData, dbError });

    if (dbError) {
      console.error("Database error in fetchDocument:", dbError);
      throw dbError;
    }

    if (!dbData) {
      console.error("No document found with ID:", documentId);
      throw new Error(`Document with ID ${documentId} not found`);
    }

    console.log("Found document in database:", dbData);

    // Initialize the document data structure
    let documentData: any = {
      id: dbData.id,
      title: dbData.title,
      client: "Unknown Client", // Will be updated if we have client data
      type: "Scoping Document",
      sections: [],
      theme: null,
    };

    // If there's a proposal file path, fetch the markdown content
    if (dbData.proposalFilePath) {
      console.log("Document has proposalFilePath:", dbData.proposalFilePath);
      try {
        const markdownContent = await fetchDocumentFile(
          dbData.proposalFilePath
        );

        console.log(
          "Fetched markdown content length:",
          markdownContent?.length || 0
        );

        // 🚨 FIXED: Use consistent parsing logic to prevent duplicates
        let theme, sections;
        if (typeof markdownContent === "object") {
          // JSON format
          ({ theme, sections } = parseJsonDocument(
            markdownContent,
            dbData.title
          ));
        } else {
          // 🚨 FIXED: Use simple H2-based parsing instead of complex parseMarkdownDocument
          // Import the consistent parsing function
          const { parseMarkdownSections } = await import('../utils/sectionUtils');
          const parsedSections = parseMarkdownSections(markdownContent);

          // Convert to the expected format
          sections = parsedSections.map((section: any, index: number) => ({
            id: section.id || `section-${index + 1}`,
            title: section.title || `Section ${index + 1}`,
            content: section.content || "",
            blocks: [], // Will be populated later
            status: "completed" as const,
            order: index + 1,
          }));

          // Extract theme from frontmatter if present
          theme = null;
          if (markdownContent.startsWith('---')) {
            const frontmatterEnd = markdownContent.indexOf('---', 3);
            if (frontmatterEnd > 0) {
              const frontmatter = markdownContent.substring(3, frontmatterEnd);
              const themeMatch = frontmatter.match(/theme:\s*([\s\S]*?)(?=\n\w|$)/);
              if (themeMatch && themeMatch[1]) {
                theme = parseSimpleTheme(themeMatch[1]);
              }
            }
          }
        }

        console.log("Parsed sections:", sections);
        console.log("Parsed theme:", theme);

        documentData.sections = sections;
        documentData.theme = theme;
      } catch (storageError) {
        console.warn(
          "Could not fetch document content from storage, using empty document:",
          storageError
        );
        // Create a default section if we can't load content
        documentData.sections = [
          {
            id: "section-1",
            title: "Document Content",
            content:
              "Content could not be loaded from file. Please edit this section.",
            status: "completed",
            order: 1,
          },
        ];
      }
    } else {
      console.log("Document has no proposalFilePath, creating default content");
      // No file path, create a default section with more helpful content
      documentData.sections = [
        {
          id: "section-1",
          title: "Introduction",
          content: `# ${dbData.title}

This document exists in your database but doesn't have saved content yet. 

**Document Details:**
- Title: ${dbData.title}
- Created: ${new Date(dbData.created_at).toLocaleDateString()}
- Status: ${dbData.status || "Draft"}

You can start editing this content right away. Use the Content Editor or Advanced Editor tabs to add sections, format text, and customize your document.

## Getting Started
1. Click on the "Content Editor" tab to add and edit sections
2. Use the "Advanced Editor" for block-based editing with drag & drop
3. Customize themes and branding in the "Theme & Branding" tab  
4. Save your changes when ready

Your edits will be automatically saved to this document.`,
          status: "completed",
          order: 1,
        },
      ];
    }

    // Try to get client name if client_id exists
    if (dbData.client_id) {
      try {
        const { data: clientData } = await supabase
          .from("crm_companies")
          .select("name")
          .eq("id", dbData.client_id)
          .single();

        if (clientData) {
          documentData.client = clientData.name;
        }
      } catch (clientError) {
        console.warn("Could not fetch client data:", clientError);
      }
    }

    return { document: documentData };
  } catch (error) {
    console.error("Error fetching document:", error);
    throw error;
  }
}

export async function fetchDocumentFile(filePath: string) {
  try {
    // Fetch markdown file directly - NO JSON fallback logic
    // Add cache-busting timestamp to prevent browser caching for scoping-proposals bucket
    const cacheBustingPath = `${filePath}?nocache=${Date.now()}`;

    // Get public URL with cache-busting parameter
    const { data: urlData } = supabase.storage
      .from("scoping-proposals")
      .getPublicUrl(cacheBustingPath);

    // Fetch directly from the public URL with no-cache headers
    const response = await fetch(urlData.publicUrl, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch document: ${response.statusText}`);
    }

    return await response.text();
  } catch (error) {
    console.error("Error fetching document file:", error);
    throw error;
  }
}

export async function saveDocument(
  documentData: GeneratedDocumentData,
  editableSections: Section[],
  documentTheme: DocumentTheme,
  documentDbId: string | null,
  clientId: string | null,
  promptTemplateId: string | null,
  scopeTemplateId: string | null,
  filePath: string | null
) {
  console.log('🔄 STEP 4 - saveDocument called:', {
    totalSections: editableSections.length,
    editedSections: editableSections.filter((s: any) => s.edited).length,
    sectionsWithContent: editableSections.filter(s => s.content && s.content.trim()).length,
    isUpdate: !!documentDbId
  });
  
  // Debug: Log content of edited sections being saved
  editableSections.forEach((section: any, index) => {
    if (section.edited) {
      console.log(`💾 SAVING Section ${index + 1} (${section.title}):`, {
        contentLength: section.content?.length || 0,
        contentPreview: section.content ? section.content.substring(0, 100) + '...' : 'NO CONTENT'
      });
    }
  });
  
  try {
    // Get current user
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError || !userData.user) {
      throw new Error("Not authenticated");
    }

    const user = userData.user;

    // Log client ID for debugging
    console.log("💾 Saving document with client ID:", clientId);
    if (clientId === user.id) {
      console.warn(
        "🚨 WARNING: Client ID matches user ID - this should not happen!"
      );
    }

    // Create a metadata object to store theme settings
    const themeMetadata = {
      colors: {
        heading: documentTheme.headingColor,
        subheading: documentTheme.subheadingColor,
        text: documentTheme.textColor,
        background: documentTheme.backgroundColor,
        accent: (documentTheme as any).accentColor || (documentTheme as any).primaryColor || '#2563eb',
      },
      typography: {
        fontFamily: documentTheme.fontFamily,
        headingSize: documentTheme.headingSize,
        textSize: documentTheme.textSize,
      },
      layout: {
        hasHeader: documentTheme.header?.enabled || false,
        hasFooter: documentTheme.footer?.enabled || false,
        headerType: documentTheme.header?.type || "simple",
        footerType: documentTheme.footer?.type || "simple",
        showCoverPage: documentTheme.showCoverPage !== false,
      },
      hasLogo: !!documentTheme.logo,
      logoData: documentTheme.logo, // Store the full base64 data string
    };

    // 1. Convert document to markdown with theme metadata
    // Add a special metadata section at the top for theme data
    let markdown = `---
theme:
  headingColor: ${documentTheme.headingColor}
  subheadingColor: ${documentTheme.subheadingColor}
  textColor: ${documentTheme.textColor}
  backgroundColor: ${documentTheme.backgroundColor}
  accentColor: ${(documentTheme as any).accentColor || (documentTheme as any).primaryColor || '#2563eb'}
  fontFamily: ${documentTheme.fontFamily}
  headingSize: ${documentTheme.headingSize}
  textSize: ${documentTheme.textSize}
  header:
    enabled: ${documentTheme.header?.enabled || false}
    type: ${documentTheme.header?.type || "simple"}
    text: ${documentTheme.header?.text || ""}
    includePageNumbers: ${documentTheme.header?.includePageNumbers || false}
  footer:
    enabled: ${documentTheme.footer?.enabled || false}
    type: ${documentTheme.footer?.type || "simple"}
    text: ${documentTheme.footer?.text || ""}
    includePageNumbers: ${documentTheme.footer?.includePageNumbers || false}
  showCoverPage: ${documentTheme.showCoverPage !== false}
  logo: ${documentTheme.logo || ""}
  brandName: ${documentTheme.brandName || ""}
  brandTagline: ${documentTheme.brandTagline || ""}
  showCoverPage: ${documentTheme.showCoverPage !== false}
---
`;

    // Add the regular markdown content
    const documentToConvert = {
      ...documentData,
      sections: editableSections,
    };
    
    console.log('🔄 STEP 5 - About to convert to markdown:', {
      sectionsCount: documentToConvert.sections.length,
      editedSectionsCount: documentToConvert.sections.filter((s: any) => s.edited).length
    });
    
    const convertedMarkdown = convertToMarkdown(documentToConvert);
    markdown += convertedMarkdown;
    
    console.log('🔄 STEP 6 - Markdown converted:', {
      markdownLength: convertedMarkdown.length,
      markdownPreview: convertedMarkdown.substring(0, 200) + '...'
    });

    // 2. Determine file path - use existing path if available
    let markdownFilePath = filePath;

    if (!markdownFilePath) {
      // If no existing file path, create new one
      const baseFileName = `${documentData.title
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase()}`;
      markdownFilePath = `${user.id}/${baseFileName}.md`;
    }

    // 3. No JSON content creation - markdown only

    // 4. Upload markdown format to Supabase Storage (NO JSON)
    const markdownUpload = await supabase.storage
        .from("scoping-proposals")
        .upload(markdownFilePath, markdown, {
          contentType: "text/markdown",
          upsert: true,
          cacheControl: 'no-cache, no-store, must-revalidate'
      });

    if (markdownUpload.error) {
      throw markdownUpload.error;
    }

    // 5. Get or create scoping_document record - FILE-BASED ONLY
    let docId = documentDbId;
    if (docId) {
      // Update existing document - ONLY metadata and file path, NO content
      const { error: updateError } = await supabase
        .from("scopingai_documents")
        .update({
          title: documentData.title,
          proposalFilePath: markdownFilePath,
          // 🚨 REMOVED: content and sections columns - content is now file-based only
          updated_at: new Date().toISOString(),
        })
        .eq("id", docId);

      if (updateError) throw updateError;
    } else {
      // Create new document - ONLY metadata and file path, NO content
      const { data: newDoc, error: insertError } = await supabase
        .from("scopingai_documents")
        .insert({
          title: documentData.title,
          proposalFilePath: markdownFilePath,
          // 🚨 REMOVED: content and sections columns - content is now file-based only
          user_id: user.id,
          client_id: clientId,
          status: "created",
        })
        .select()
        .single();

      if (insertError) throw insertError;
      docId = newDoc.id;
    }

    return { documentId: docId, filePath: null };
  } catch (error) {
    console.error("Error saving document:", error);
    throw error;
  }
}

export async function regenerateSectionWithAI(
  sectionContent: string,
  sectionTitle: string,
  enhancementType: string,
  projectContext: string
) {
  try {
    const response = await axios.post("/api/regenerate-section", {
      sectionContent,
      sectionTitle,
      enhancementType,
      projectContext,
    });

    return response.data.regeneratedContent;
  } catch (error) {
    console.error("Error regenerating section:", error);
    throw error;
  }
}

// REMOVED: parseJsonDocument function - no longer needed since we only use markdown files

function parseMarkdownDocument(markdownContent: string, fallbackTitle: string) {
  const lines = markdownContent.split("\n");
  let themeData = null;
  let contentStart = 0;

  // Check if document starts with frontmatter
  if (lines[0] === "---") {
    let frontmatterEnd = -1;
    for (let i = 1; i < lines.length; i++) {
      if (lines[i] === "---") {
        frontmatterEnd = i;
        break;
      }
    }

    if (frontmatterEnd > 0) {
      const frontmatter = lines.slice(1, frontmatterEnd).join("\n");
      try {
        // Simple YAML parser for theme data
        const themeMatch = frontmatter.match(/theme:\s*([\s\S]*?)(?=\n\w|$)/);
        if (themeMatch && themeMatch[1]) {
          themeData = parseSimpleTheme(themeMatch[1]);
        }
      } catch (error) {
        console.warn("Failed to parse theme data:", error);
      }
      contentStart = frontmatterEnd + 1;
    }
  }

  // Parse content starting after frontmatter
  const contentLines = lines.slice(contentStart);
  let title = fallbackTitle;
  let client = "";
  let type = "Generated Document";
  const sections: Section[] = [];
  let currentSection: Section | null = null;
  let currentContent: string[] = [];

  // Parse content with improved formatting preservation
  for (let i = 0; i < contentLines.length; i++) {
    const line = contentLines[i];

    // Extract title (H1)
    if (line.startsWith("# ")) {
      title = line.substring(2).trim();
      continue;
    }

    // Extract metadata
    if (line.startsWith("**Client:**")) {
      client = line.replace("**Client:**", "").trim();
      continue;
    }

    if (
      line.startsWith("**Project Type:**") ||
      line.startsWith("**Document Type:**")
    ) {
      type = line.replace(/\*\*(Project Type|Document Type):\*\*/, "").trim();
      continue;
    }

    // Skip separator lines
    if (line.trim() === "---") {
      continue;
    }

    // Section headers (H2) - but be more selective
    if (line.startsWith("## ")) {
      const sectionTitle = line.substring(3).trim().toLowerCase();

      // Only create new sections for major section headings
      // This prevents subsections from becoming separate sections
      const isMajorSection =
        sectionTitle.includes("introduction") ||
        sectionTitle.includes("overview") ||
        sectionTitle.includes("executive summary") ||
        sectionTitle.includes("project scope") ||
        sectionTitle.includes("scope of work") ||
        sectionTitle.includes("deliverables") ||
        sectionTitle.includes("timeline") ||
        sectionTitle.includes("schedule") ||
        sectionTitle.includes("budget") ||
        sectionTitle.includes("pricing") ||
        sectionTitle.includes("investment") ||
        sectionTitle.includes("methodology") ||
        sectionTitle.includes("approach") ||
        sectionTitle.includes("implementation") ||
        sectionTitle.includes("next steps") ||
        sectionTitle.includes("conclusion") ||
        sectionTitle.includes("appendix") ||
        sectionTitle.includes("terms") ||
        sectionTitle.includes("conditions") ||
        sections.length === 0; // Always create first section

      if (isMajorSection) {
        // Save previous section if exists
        if (currentSection) {
          currentSection.content = currentContent
            .join("\n")
            .replace(/\n{3,}/g, "\n\n") // Clean up excessive newlines
            .trim();

          // Create blocks from content for compatibility
          currentSection.blocks = parseContentToBlocks(currentSection.content);
          sections.push(currentSection);
        }

        // Start new section
        currentSection = {
          id: `section-${sections.length + 1}`,
          title: line.substring(3).trim(), // Use original casing
          content: "",
          blocks: [],
          status: "completed" as const,
          order: sections.length + 1,
        };
        currentContent = [];
      } else {
        // Treat as content within the current section (subsection)
        if (currentSection) {
          currentContent.push(line);
        }
      }
      continue;
    }

    // Add content to current section
    if (currentSection) {
      currentContent.push(line);
    } else if (line.trim()) {
      // If we don't have a section yet but have content, create a default section
      currentSection = {
        id: `section-1`,
        title: "Introduction",
        content: "",
        blocks: [],
        status: "completed" as const,
        order: 1,
      };
      currentContent = [line];
    }
  }

  // Add the last section
  if (currentSection) {
    currentSection.content = currentContent
      .join("\n")
      .replace(/\n{3,}/g, "\n\n") // Clean up excessive newlines
      .trim();

    // Create blocks from content for compatibility
    currentSection.blocks = parseContentToBlocks(currentSection.content);
    sections.push(currentSection);
  }

  // If no sections were created, create a default one
  if (sections.length === 0) {
    const defaultContent =
      contentLines.join("\n").trim() || "No content available.";
    sections.push({
      id: "section-1",
      title: "Document Content",
      content: defaultContent,
      blocks: parseContentToBlocks(defaultContent),
      status: "completed" as const,
      order: 1,
    });
  }

  console.log(
    `📄 Parsed ${sections.length} sections from markdown:`,
    sections.map((s) => s.title)
  );

  return {
    title,
    client,
    type,
    sections,
    theme: themeData,
  };
}

// Helper function to convert Editor.js JSON to blocks with full support for all block types
export function convertEditorJsonToBlocks(editorJson: any): Block[] {
  if (!editorJson?.blocks) return [];

  return editorJson.blocks.map((editorBlock: any, index: number) => {
    const block: Block = {
      id: editorBlock.id || `block-${index + 1}`,
      type: "text",
      content: "",
      order: index,
    };

    switch (editorBlock.type) {
      case "header":
        block.type = "header";
        block.content = editorBlock.data?.text || "";
        block.level = editorBlock.data?.level || 2;
        break;

      case "paragraph":
        block.type = "text";
        block.content = editorBlock.data?.text || "";
        break;

      case "list":
        block.type = "list";
        block.content = "";

        // Enhanced list processing to handle all list types including checklists
        const listData = editorBlock.data || {};
        const listStyle = listData.style || "unordered";
        const rawItems = listData.items || [];

        if (listStyle === "checklist") {
          // Handle checklist items with checked state
          const processedItems = rawItems
            .filter((item: any) => item !== null && item !== undefined)
            .map((item: any) => {
              let itemText = "";
              let isChecked = false;

              if (typeof item === "string") {
                itemText = item;
              } else if (typeof item === "object") {
                itemText = item.text || item.content || "";
                isChecked = item.checked === true;
              }

              // Clean up text and add checkbox prefix for display
              itemText = itemText.replace(/<[^>]*>/g, "").trim();
              if (!itemText || itemText.includes("[object Object]")) return "";

              // Add checkbox indicator to the text for rendering
              return isChecked ? `☑ ${itemText}` : `☐ ${itemText}`;
            })
            .filter((item: string) => item !== "");

          block.items = processedItems;
        } else {
          // Handle regular unordered and ordered lists
          const processedItems = rawItems
            .filter((item: any) => item !== null && item !== undefined)
            .map((item: any) => {
              if (typeof item === "string") {
                return item;
              } else if (typeof item === "object" && item.text) {
                return item.text;
              } else if (typeof item === "object" && item.content) {
                return item.content;
              } else if (typeof item === "object") {
                console.warn(
                  "List item is an object in convertEditorJsonToBlocks:",
                  item
                );
                return JSON.stringify(item);
              } else {
                return String(item);
              }
            })
            .map((item: string) => item.replace(/<[^>]*>/g, "").trim())
            .filter(
              (item: string) => item !== "" && !item.includes("[object Object]")
            );

          block.items = processedItems;
        }

        block.style = listStyle;
        break;

      case "image":
        block.type = "image";
        const imageData = editorBlock.data || {};
        block.content = imageData.url || imageData.file?.url || "";
        block.caption = imageData.caption || "";
        break;

      case "threeColumns":
      case "columns":
        // Handle column layouts - convert to a special text block with column data
        block.type = "text";
        const columnsData = editorBlock.data || {};
        const columns = columnsData.columns || [];

        // Convert columns to a readable text representation
        if (columns.length > 0) {
          const columnTexts = columns.map((column: any, colIndex: number) => {
            const columnBlocks = column.blocks || [];
            const columnContent = columnBlocks
              .map((colBlock: any) => {
                switch (colBlock.type) {
                  case "header":
                    return colBlock.data?.text || "";
                  case "paragraph":
                    return colBlock.data?.text || "";
                  case "list":
                    const items = colBlock.data?.items || [];
                    return items
                      .map((item: any) =>
                        typeof item === "string"
                          ? `• ${item}`
                          : `• ${item.text || item.content || ""}`
                      )
                      .join("\n");
                  default:
                    return colBlock.data?.text || colBlock.data?.content || "";
                }
              })
              .filter((text) => text.trim())
              .join("\n\n");

            return `**Column ${colIndex + 1}:**\n${
              columnContent || "Empty column"
            }`;
          });

          block.content = `[Three-Column Layout]\n\n${columnTexts.join(
            "\n\n---\n\n"
          )}`;
        } else {
          block.content = "[Empty Three-Column Layout]";
        }
        break;

      case "quote":
        block.type = "quote";
        block.content = editorBlock.data?.text || "";
        block.caption = editorBlock.data?.caption || "";
        break;

      case "code":
        block.type = "code";
        block.content = editorBlock.data?.code || "";
        break;

      case "delimiter":
        block.type = "text";
        block.content = "---";
        break;

      default:
        block.type = "text";
        block.content =
          editorBlock.data?.text || editorBlock.data?.content || "";
    }

    return block;
  });
}

// Helper function to create blocks from content for compatibility
function parseContentToBlocks(content: string): Block[] {
  const blocks: Block[] = [];
  const lines = content.split("\n");
  let currentBlock: Block | null = null;
  let blockId = 0;

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      // Empty line - finish current block
      if (currentBlock) {
        blocks.push(currentBlock);
        currentBlock = null;
      }
      continue;
    }

    // Detect block types
    if (trimmedLine.startsWith("###")) {
      // H3 header
      if (currentBlock) blocks.push(currentBlock);
      currentBlock = {
        id: `block-${++blockId}`,
        type: "header",
        content: trimmedLine.substring(3).trim(),
        level: 3,
      };
    } else if (trimmedLine.startsWith("- ") || trimmedLine.startsWith("* ")) {
      // List item
      if (currentBlock && currentBlock.type === "list") {
        currentBlock.items = currentBlock.items || [];
        currentBlock.items.push(trimmedLine.substring(2).trim());
      } else {
        if (currentBlock) blocks.push(currentBlock);
        currentBlock = {
          id: `block-${++blockId}`,
          type: "list",
          content: "",
          items: [trimmedLine.substring(2).trim()],
        };
      }
    } else {
      // Regular text
      if (currentBlock && currentBlock.type === "text") {
        currentBlock.content += "\n" + trimmedLine;
      } else {
        if (currentBlock) blocks.push(currentBlock);
        currentBlock = {
          id: `block-${++blockId}`,
          type: "text",
          content: trimmedLine,
        };
      }
    }
  }

  // Add the last block
  if (currentBlock) {
    blocks.push(currentBlock);
  }

  return blocks;
}

function parseSimpleTheme(themeData: string): DocumentTheme {
  // Enhanced parser for theme YAML data with nested objects
  const theme: Record<string, any> = {};
  const lines = themeData.split("\n");
  let currentSection: string | null = null;

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.includes(":")) {
      const [key, value] = trimmed.split(":").map((s) => s.trim());

      if (key && value !== undefined) {
        // Check if this is a section header (like "header:", "footer:", "logo:")
        if (value === "" || value === null) {
          currentSection = key;
          theme[key] = {};
        } else {
          // Remove quotes if present
          const cleanValue = value.replace(/['"]/g, "");
          const parsedValue =
            cleanValue === "true"
              ? true
              : cleanValue === "false"
              ? false
              : !isNaN(Number(cleanValue))
              ? Number(cleanValue)
              : cleanValue;

          if (currentSection) {
            // This is a nested property
            theme[currentSection][key] = parsedValue;
          } else {
            // This is a top-level property
            theme[key] = parsedValue;
          }
        }
      }
    }
  }

  // Ensure logo object exists even if empty
  if (!theme.logo) {
    theme.logo = {};
  }

  return theme as DocumentTheme;
}

/**
 * Helper function to remove duplicate section headings from content
 */
function removeDuplicateHeading(content: string, sectionTitle: string): string {
  if (!content || !sectionTitle) return content;

  // Escape special regex characters in section title
  const escapedTitle = sectionTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Pattern to match H2 heading at the start of content (case insensitive)
  const duplicateHeadingPattern = new RegExp(`^##\\s+${escapedTitle}\\s*\\n?`, 'i');

  if (duplicateHeadingPattern.test(content)) {
    console.log(`🔧 Removing duplicate H2 heading: "## ${sectionTitle}"`);
    return content.replace(duplicateHeadingPattern, '').trim();
  }

  // Also check first line for exact match
  const lines = content.split('\n');
  if (lines.length > 0) {
    const firstLine = lines[0].trim();
    const expectedHeading = `## ${sectionTitle}`;

    if (firstLine.toLowerCase() === expectedHeading.toLowerCase()) {
      console.log(`🔧 Removing duplicate heading from first line: "${firstLine}"`);
      lines.shift(); // Remove first line
      return lines.join('\n').trim();
    }
  }

  return content;
}

// Simple markdown converter
function convertToMarkdown(document: GeneratedDocumentData): string {
  let markdown = "";

  // Add title as main heading
  markdown += `# ${document.title}\n\n`;

  // Add document metadata in a clean format
  if (document.client) {
    markdown += `**Client:** ${document.client}\n\n`;
  }

  if (document.type && document.type !== "Generated Document") {
    markdown += `**Project Type:** ${document.type}\n\n`;
  }

  // Add a separator line for better visual structure
  markdown += `---\n\n`;

  // Process sections with proper formatting
  for (const section of document.sections) {
    // Section title as H2
    markdown += `## ${section.title}\n\n`;

    // Process section content - preserve formatting and structure
    let sectionContent = section.content;

    // 🚨 FIXED: Remove duplicate section headings from AI-generated content
    sectionContent = removeDuplicateHeading(sectionContent, section.title);

    // Clean up content formatting
    sectionContent = sectionContent
      .replace(/\*\*(.*?)\*\*/g, "**$1**") // Preserve bold formatting
      .replace(/\n{3,}/g, "\n\n") // Remove excessive newlines
      .replace(/^\s+|\s+$/g, "") // Trim whitespace
      .replace(/•/g, "-") // Convert bullet points to markdown
      .replace(/\n-\s/g, "\n- "); // Fix bullet point spacing

    // Add section content with proper spacing
    markdown += `${sectionContent}\n\n`;

    // Log the processing for debugging
    console.log(`📝 Processed section "${section.title}": ${sectionContent.length} characters`);

    // Add separator between sections (except last one)
    if (document.sections.indexOf(section) < document.sections.length - 1) {
      markdown += `---\n\n`;
    }
  }

  // Clean up final markdown
  markdown =
    markdown
      .replace(/\n{3,}/g, "\n\n") // Remove excessive newlines
      .replace(/---\n\n$/, "") // Remove trailing separator
      .trim() + "\n"; // Ensure single newline at end

  return markdown;
}

// Export Document Services
export const exportDocumentToPDF = async (
  documentId: string
): Promise<Blob> => {
  try {
    const response = await api.get(
      ENDPOINTS.EXPORT_PDF.replace(":id", documentId),
      {
        responseType: "blob",
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error exporting document to PDF:", error);
    throw error;
  }
};

export const exportDocumentToWord = async (
  documentId: string
): Promise<Blob> => {
  try {
    const response = await api.get(
      ENDPOINTS.EXPORT_WORD.replace(":id", documentId),
      {
        responseType: "blob",
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error exporting document to Word:", error);
    throw error;
  }
};

// Client Management Services
export const fetchClients = async (): Promise<Client[]> => {
  try {
    const response = await api.get(ENDPOINTS.GET_CLIENTS);
    return response.data;
  } catch (error) {
    console.error("Error fetching clients:", error);
    throw error;
  }
};

export const saveClient = async (
  clientData: Omit<Client, "id">
): Promise<Client> => {
  try {
    const response = await api.post(ENDPOINTS.CREATE_CLIENT, clientData);
    return response.data;
  } catch (error) {
    console.error("Error saving client:", error);
    throw error;
  }
};

// Prompt Template Services
export const fetchPromptTemplates = async (): Promise<PromptTemplate[]> => {
  try {
    const response = await api.get(ENDPOINTS.PROMPT_TEMPLATES);
    return response.data;
  } catch (error) {
    console.error("Error fetching prompt templates:", error);
    throw error;
  }
};

export const savePromptTemplate = async (
  promptData: Omit<PromptTemplate, "id" | "created_at" | "updated_at">
): Promise<PromptTemplate> => {
  try {
    const response = await api.post(ENDPOINTS.PROMPT_TEMPLATES, promptData);
    return response.data;
  } catch (error) {
    console.error("Error saving prompt template:", error);
    throw error;
  }
};

// Reference Document Services
export const fetchReferenceDocuments = async (): Promise<
  ReferenceDocument[]
> => {
  try {
    const response = await api.get(ENDPOINTS.REFERENCE_DOCUMENTS);
    return response.data;
  } catch (error) {
    console.error("Error fetching reference documents:", error);
    throw error;
  }
};

export const fetchReferenceDocument = async (
  docId: string
): Promise<ReferenceDocument | null> => {
  try {
    const response = await api.get(
      ENDPOINTS.REFERENCE_DOCUMENT_BY_ID.replace(":id", docId)
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching reference document:", error);
    return null;
  }
};

// Document Library Services
export const fetchDocumentLibrary = async (): Promise<any[]> => {
  try {
    const response = await api.get(ENDPOINTS.DOCUMENT_LIBRARY);
    return response.data;
  } catch (error) {
    console.error("Error fetching document library:", error);
    throw error;
  }
};

export const deleteDocument = async (documentId: string): Promise<void> => {
  try {
    await api.delete(ENDPOINTS.DOCUMENT_BY_ID.replace(":id", documentId));
  } catch (error) {
    console.error("Error deleting document:", error);
    throw error;
  }
};

// Utility function to prepare document data for generation
export const prepareDocumentData = (
  projectInfo: any,
  clientData: any,
  selectedClient: any,
  selectedTemplate: string,
  referenceDocument: any,
  requirementsOption: string,
  requirementsText: string,
  selectedKnowledgeDocuments: string[],
  documentRequirements: Record<string, string>,
  aiPrompts: any,
  templates: any[],
  user?: any, // Add user parameter to get user ID for clientId
  documentLogo?: any, // Add logo parameter
  documentBranding?: any, // Add branding parameter
  tenderId?: string | null // Tender ID for cost data lookup
): DocumentGenerationData => {
  // Check if we have enhanced AI prompts from the DocumentOverviewStep
  const enhancedAiPrompts = (window as any).enhancedAiPrompts;
  const finalAiPrompts = enhancedAiPrompts || aiPrompts;

  // Log the prompt information for debugging
  if (enhancedAiPrompts) {
    console.log(
      "🎯 Using enhanced AI prompts for generation:",
      enhancedAiPrompts
    );
  } else {
    console.log("📝 Using default AI prompts for generation:", aiPrompts);
  }

  // Determine the client ID - use selected client ID or client data ID, DO NOT use user ID as fallback
  const clientId = selectedClient?.id || clientData.id || null;

  console.log("🔍 Client ID resolution:", {
    clientDataId: clientData.id,
    selectedClientId: selectedClient?.id,
    userId: user?.id,
    finalClientId: clientId,
    clientDataName: clientData.name,
    selectedClientName: selectedClient?.name,
    warning:
      clientId === user?.id
        ? "WARNING: Client ID matches User ID - this is incorrect!"
        : clientId
        ? "✅ Valid client ID from CRM contacts"
        : "⚠️ No client ID - will be saved as null",
  });

  const foundTemplate = templates.find((t) => t.id === selectedTemplate);
  console.log("📋 Template resolution debug:", {
    selectedTemplate,
    templatesCount: templates.length,
    templateIds: templates.map((t) => t.id),
    foundTemplate: foundTemplate
      ? {
          id: foundTemplate.id,
          title: foundTemplate.title,
          name: foundTemplate.name,
          sectionsCount: foundTemplate.sections?.length,
        }
      : null,
    referenceDocument: referenceDocument
      ? {
          id: referenceDocument.id,
          title: referenceDocument.title,
          sectionsCount: referenceDocument.sections?.length,
        }
      : null,
  });

  return {
    title: projectInfo.title,
    client: {
      id: clientId,
      name: clientData.name,
      contactPerson: clientData.contactPerson,
      email: clientData.email,
      phone: clientData.phone,
      industry: clientData.industry,
      company: clientData.company,
    },
    template: (() => {
      // Handle existing documents (prefixed with 'doc_')
      if (selectedTemplate.startsWith("doc_") && referenceDocument) {
        // Convert sections to string array for LangGraph compatibility
        const convertedSections = (referenceDocument.sections || []).map(
          (section: any) => {
            if (typeof section === "string") {
              return section;
            } else if (section && typeof section === "object") {
              // Extract title from object sections
              return (
                section.title ||
                section.name ||
                section.heading ||
                "Untitled Section"
              );
            } else {
              return "Untitled Section";
            }
          }
        );

        console.log(
          "📋 Converting existing document sections in prepareDocumentData:",
          {
            originalSections: referenceDocument.sections,
            convertedSections,
            sectionCount: convertedSections.length,
            templateId: selectedTemplate,
          }
        );

        return {
          id: selectedTemplate,
          name: referenceDocument.title || "Existing Document Template",
          sections: convertedSections,
          type: "existing_document",
          source_document_id: referenceDocument.id,
          // Store original sections for content reference
          original_sections: referenceDocument.sections,
          // Ensure AI generates new content, doesn't preserve original
          generate_new_content: true,
        };
      }

      // Handle regular templates
      const foundTemplate = templates.find((t) => t.id === selectedTemplate);

      // Get content from either direct property or rawData (for default templates)
      const templateContent =
        foundTemplate?.content || foundTemplate?.rawData?.content || "";

      console.log("📋 Template content retrieval:", {
        templateId: selectedTemplate,
        foundTemplate: !!foundTemplate,
        hasDirectContent: !!foundTemplate?.content,
        hasRawDataContent: !!foundTemplate?.rawData?.content,
        contentLength: templateContent.length,
        contentPreview: templateContent.substring(0, 100),
      });

      return {
        id: selectedTemplate,
        name: foundTemplate?.title || foundTemplate?.name || "",
        sections: foundTemplate?.sections || [],
        content: templateContent,
        description:
          foundTemplate?.description ||
          foundTemplate?.rawData?.metadata?.description ||
          "",
        category: foundTemplate?.category || foundTemplate?.type || "",
        isDefault:
          foundTemplate?.isDefault ||
          foundTemplate?.rawData?.metadata?.isDefault ||
          false,
        type: "standard",
      };
    })(),
    referenceDocument: referenceDocument,
    preserveOriginalContent: false,
    requirements: {
      method: requirementsOption,
      projectRequirements:
        requirementsOption === "requirements" ? requirementsText : "",
      hasUploadedFiles: requirementsOption === "upload",
      selectedKnowledgeDocuments:
        requirementsOption === "knowledge"
          ? selectedKnowledgeDocuments
          : undefined,
      documentRequirements:
        requirementsOption === "knowledge" ? documentRequirements : undefined,
    },
    aiPrompts: finalAiPrompts, // Use enhanced prompts if available
    project: {
      title: projectInfo.title,
      startDate: projectInfo.startDate,
      endDate: projectInfo.endDate,
      description: projectInfo.description,
      budget: (() => {
        // Format budget range if both min and max are provided
        if (projectInfo.budgetMin && projectInfo.budgetMax) {
          return `$${projectInfo.budgetMin.toLocaleString()} - $${projectInfo.budgetMax.toLocaleString()}`;
        }
        // Format single budget value
        if (projectInfo.budgetMin) {
          return `$${projectInfo.budgetMin.toLocaleString()}+`;
        }
        if (projectInfo.budgetMax) {
          return `Up to $${projectInfo.budgetMax.toLocaleString()}`;
        }
        // Fallback to default budget categories
        return projectInfo.budget || "To be discussed";
      })(),
    },
    tenderId: tenderId || null,
  };
};
